/**
 * Arıza DM-17 metinleri (ADR 0060): liste, kayıt (General, Part A/B/C, prosedür, durum ve
 * tarihler, imzalar, faaliyetler, takip, kapanış), ofis notu, PDF ve hata kodları iki dilde
 * (`defects.test.tsx`) [NR496 4.1.5].
 */

/** Ekranların alabildiği hata kodları; `defects.test.tsx` iki dilde metnini arar. */
export const DEFECT_ERROR_CODES = [
  'GENERIC',
  'NETWORK',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'ROW_VERSION_CONFLICT',
  'REAUTHENTICATION_REQUIRED',
  'INVALID_CREDENTIALS',
  'INVALID_DEFECT',
  'DESCRIPTION_REQUIRED',
  'SYSTEM_REQUIRED',
  'EQUIPMENT_REQUIRED',
  'EQUIPMENT_DELETED',
  'TARGET_BEFORE_REPORTED',
  'DATE_BEFORE_REPORTED',
  'FUTURE_DATE',
  'PROCEDURE_REVIEW_INCOMPLETE',
  'ACTION_DESCRIPTION_REQUIRED',
  'EVIDENCE_REQUIRED',
  'COMPLETED_DATE_REQUIRED',
  'CANCEL_REASON_REQUIRED',
  'JOB_EQUIPMENT_MISMATCH',
  'JOB_DELETED',
  'INVALID_SOURCE',
  'ALREADY_CONVERTED',
  'DEFECT_FIXED',
  'INVALID_TRANSITION',
  'DEFECT_CLOSED',
  'DEFECT_PENDING_CLOSURE',
  'ALREADY_SUBMITTED',
  'NOT_IN_VERIFICATION',
  'ROOT_CAUSE_REQUIRED',
  'CORRECTIVE_ACTION_REQUIRED',
  'REPAIR_NOT_REPORTED',
  'REPAIR_REQUIRED',
  'CONDITION_AFTER_REQUIRED',
  'NOT_OPERATIONAL',
  'CLASS_NOTIFICATION_REQUIRED',
  'ACTIONS_OPEN',
  'CAPTAIN_ACTION_REQUIRED',
  'RECTIFIED_DATE_REQUIRED',
  'PROCEDURE_REVIEW_REQUIRED',
  'REPAIR_WORK_ORDER_EXISTS',
  'STALE_DECISION',
  'SELF_APPROVAL',
  'NOT_PENDING',
  'BODY_REQUIRED',
  'FILE_NOT_UPLOADED',
  // LGS-17 (ADR 0096)
  'REASON_REQUIRED',
  'NEGATIVE_STOCK',
  'STOCK_ITEM_UNKNOWN',
  'INVALID_STOCK_MOVEMENT',
  'WORK_ORDERS_OPEN',
] as const;

export const defectsEn = {
  title: 'Defects',
  description: 'Equipment defect notification and rectification (DM-17): report, act, sign off.',
  kinds: { defect: 'Defect', breakdown: 'Breakdown' },
  stages: {
    reported: 'Reported',
    under_action: 'Under Action',
    verification: 'Verification',
    closed: 'Closed',
  },
  awaitingParts: 'Under Action – Awaiting Parts',
  statuses: {
    open: 'Open',
    investigating: 'Investigating',
    repair: 'Under repair',
    testing: 'Rectified – testing',
    verification: 'Verification',
  },
  priorities: { low: 'Low', medium: 'Medium', high: 'High' },
  severities: { minor: 'Minor', major: 'Major', critical: 'Critical' },
  departments: {
    deck: 'Deck',
    engine: 'Engine',
    electrical: 'Electrical',
    navigation: 'Navigation',
    safety: 'Safety',
    accommodation: 'Accommodation',
    galley: 'Galley',
    other: 'Other',
  },
  conditions: {
    operational: 'Operational',
    operational_with_restrictions: 'Operational with restrictions',
    not_operational: 'Not operational',
  },
  actionStatuses: {
    open: 'Open',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },
  pendingApproval: 'Pending approval',
  pendingApprovalRole: 'Awaiting {{role}} ({{where}})',
  overdue: 'Overdue',
  list: {
    newDefect: 'New Defect',
    filters: 'Defect filters',
    search: 'Search',
    searchPlaceholder: 'Search by no., equipment, system or location…',
    status: 'Stage',
    allStatus: 'All stages',
    priority: 'Priority',
    allPriority: 'All priorities',
    kind: 'Type',
    allKinds: 'All types',
    vessel: 'Vessel',
    allVessels: 'All vessels',
    dateRange: 'Reported between',
    dateFrom: 'From',
    dateTo: 'To',
    table: 'Defect reports',
    open: 'Open {{no}}',
    emptyTitle: 'No defects reported',
    emptyShip: 'Report a defect with “New Defect”; it can be linked to equipment now or later.',
    emptyWeb: 'Defects reported on board appear here after the next synchronisation.',
    noMatch: 'No defect matches the filters.',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    pagination: 'Defect pages',
    error: 'The defects could not be loaded.',
    retry: 'Try again',
    kpi: {
      total: 'Total',
      open: 'Open',
      overdue: 'Overdue',
      pending: 'In Approval',
      closed: 'Closed',
    },
    columns: {
      defectNo: 'Defect No',
      reported: 'Reported',
      vessel: 'Vessel',
      equipment: 'Equipment / System',
      description: 'Description',
      department: 'Department',
      priority: 'Priority',
      stage: 'Stage',
      target: 'Target',
    },
  },
  record: {
    title: 'Defect Report',
    subtitle: 'Equipment Defect Notification and Rectification (DM-17)',
    newTitle: 'New Defect Report',
    back: 'Back to defects',
    loading: 'Loading the defect report…',
    print: 'Print Form (DM-17)',
    save: 'Save',
    create: 'Report Defect',
    saved: 'Saved.',
    unsaved: 'You have unsaved changes.',
    readOnly: 'Read-only: defects are written on board.',
    locked: 'Locked: the closure is waiting for approval or approved.',
    stageMenu: 'Change stage',
    moveTo: 'Move to: {{status}}',
    sections: 'Report sections',
    tabs: {
      general: '1. General',
      crew: '2. Defect Details (Crew)',
      captain: '3. Rectification (Captain)',
      management: '4. Management',
      documentation: '5. System Documentation',
      closure: '6. Follow-up & Closure',
    },
    cards: {
      general: 'General Information',
      partA: 'Part A – Description of Defect',
      partAHint: '(To be completed by crew member)',
      partB: 'Part B – Action Taken by Captain',
      partC: 'Part C – Action Taken by Management',
      review: 'IMS / SMS – Procedure Review (if required)',
      status: 'Status & Key Dates',
      related: 'Related Information',
      signatures: 'Signatures',
      actions: 'Corrective Actions',
      followups: 'Follow-up History',
      closure: 'Investigation & Closure',
    },
    fields: {
      recordNo: 'Record No.',
      kind: 'Type',
      dateReported: 'Date Reported',
      reportedBy: 'Reported By',
      position: 'Position',
      vessel: 'Vessel',
      imo: 'IMO No.',
      department: 'Department',
      location: 'Location on Board',
      category: 'Engine / Equipment Category',
      equipment: 'Equipment / System',
      equipmentNone: 'Not in PMS (enter the system)',
      systemName: 'System (not in PMS)',
      tag: 'Equipment Tag No.',
      job: 'Linked PMS Task',
      jobNone: 'No linked task',
      severity: 'Severity',
      operationalImpact: 'Operational impact',
      description: 'Description of defect',
      captainAction: 'Action taken by captain',
      captainActionOn: 'Date Action Taken',
      rectifiedOn: 'Date Rectified',
      targetOn: 'Target Completion Date',
      closedOn: 'Closed Date',
      priority: 'Priority',
      reviewQuestion: 'Does this defect require any update to SMS documentation or procedures?',
      yes: 'Yes',
      no: 'No',
      procedure: 'Name of Procedures',
      improvement: 'How the procedure can be improved',
      investigation: 'Investigation',
      rootCause: 'Root cause',
      correctiveAction: 'Corrective action (summary)',
      repairWorkOrder: 'Repair work order',
      noRepairReason: 'No repair needed – reason',
      testResult: 'Test result',
      conditionAfter: 'Condition after repair',
      classNotificationRef: 'Class notification reference',
      classNotificationWaiver: 'Class notification not required – reason',
      source: 'Source',
    },
    sources: {
      manual: 'Reported on board',
      measurement: 'Failed measurement',
      work_record: 'Maintenance record',
      cm_import: 'Condition monitoring import',
      daily_report: 'Daily observation',
      hse_report: 'HSEQ report',
      drydock: 'Pre-Docking finding',
    },
    observation: 'Converted from the daily observation of {{day}}: “{{summary}}”',
    observationLink: 'Open daily report',
    alreadyConverted: 'This observation has already been converted to {{no}}.',
    hseTitle: 'Missing equipment is not a defect',
    hseBody:
      'Equipment that should be on board but is missing, short or without a valid certificate is an ISM deficiency. Report it as an HSE Deficiency; a defect (DM-17) is a failure of existing equipment.',
    hseOpen: 'Open HSE Deficiency',
    managementEmpty: 'No action recorded by management yet.',
    managementOffice:
      'Management actions are recorded by the office and arrive with the next synchronisation.',
    addManagementAction: 'Add Management Action',
    addComment: 'Add Comment',
    comments: 'Office comments',
    documentsEmpty: 'No photos or documents yet.',
    addPhoto: 'Add Photo / Document',
    uploading: 'Uploading…',
    repairOpen: 'Create Work Order',
    repairNeedsEquipment: 'Link the defect to equipment to create a repair work order.',
    submit: 'Submit for Closure',
    approve: 'Approve',
    return: 'Return',
  },
  // LGS-17 sadeleştirmesi (ADR 0096)
  lgs: {
    eyebrow: 'Defect Report (LGS-17)',
    created: 'Created: {{at}}',
    source: 'Source: {{source}}',
    createdBy: 'Created by: {{name}}',
    print: 'Print',
    pdf: 'PDF',
    sourceDirect: 'Direct defect report',
    sourceHse: '{{type}} {{no}}',
    sourceWorkOrder: 'PMS maintenance {{no}}',
    sourceDaily: 'Daily observation {{no}}',
    tabsLabel: 'Defect report tabs',
    tabs: {
      form: 'Form (LGS-17)',
      workflow: 'Workflow / History',
      workOrders: 'Work Orders ({{n}})',
      nonConformities: 'Non-Conformities ({{n}})',
      material: 'Material ({{n}})',
      photos: 'Photos ({{n}})',
      documents: 'Related Documents',
    },
    parts: {
      a: 'PART A – Crew Member',
      aHint: 'To be completed by the crew member',
      b: 'PART B – Captain',
      bHint: 'To be completed by the captain',
      c: 'PART C – Management',
      cHint: 'To be completed by the office',
      sms: 'Safety Management System Follow-up',
      smsHint: 'SMS procedure follow-up',
      completed: 'Completed',
      waiting: 'Waiting',
    },
    fields: {
      crewName: 'Crew member name',
      position: 'Position / Rank',
      equipment: 'Equipment',
      location: 'Location',
      description: 'Description of defect',
      actionRequired: 'Action required to rectify defect',
      actionTaken: 'Action taken to rectify the defect',
      dateRectified: 'Date defect rectified',
      procedures: 'Name of procedures',
      improvement: 'How the procedure(s) can be improved',
      noUpdate: 'No SMS procedure update required',
      crewSignature: 'Signature of crew',
      captainSignature: 'Signature of captain',
      managementApproval: 'Management approval',
      date: 'Date',
      you: 'You (signed-in user)',
    },
    managementShip: 'Part C is written by the office and arrives with the next synchronisation.',
    photos: 'Photos and documents',
    more: 'More details',
    moreHint:
      'Type, severity, priority, department, target date, PMS task, investigation, root cause, class notification and closure fields.',
    side: {
      related: 'Related Records',
      status: 'Status',
      steps: 'Workflow',
      quick: 'Quick Actions',
      relatedEmpty: 'No related records yet.',
      current: 'Current status',
      priority: 'Priority',
      category: 'Category',
      createdBy: 'Created by',
      createdAt: 'Created',
      updatedAt: 'Last update',
      kinds: {
        workOrder: 'Work Order',
        nonConformity: 'Non-Conformity Report',
        requisition: 'Purchase Requisition',
        equipment: 'Equipment',
        source: 'Source',
      },
      quickNone: 'No actions for your role at this stage.',
    },
    quick: {
      newWorkOrder: 'New Work Order',
      openNc: 'Open Non-Conformity Report',
      newRequisition: 'Create Purchase Requisition',
      useStock: 'Use from Stock',
      moveToVerification: 'Move to Verification',
      rejectRepair: 'Reject Repair',
      needsEquipment: 'Link the defect to equipment to open a work order.',
    },
    workflow: {
      title: 'Defect workflow',
      table: 'Defect workflow steps',
      columns: {
        no: '#',
        stage: 'Stage',
        step: 'Step',
        owner: 'Responsible',
        state: 'Status',
        when: 'Date / By',
      },
      steps: {
        create: 'Defect created (Part A)',
        captainReview: 'Captain assessment (Part B starts)',
        workOrder: 'Work order created',
        nonConformity: 'Non-conformity raised (if required)',
        parts: 'Spare parts / purchase',
        execution: 'Work order execution',
        completion: 'Work order completion',
        verification: 'Captain repair verification (Part B complete)',
        management: 'Management review (Part C)',
        sms: 'SMS procedure follow-up',
        closure: 'Defect closure',
      },
      owners: {
        crew: 'Crew',
        master: 'Captain',
        technical: 'Technical staff',
        technical_office: 'Technical staff / Office',
        office: 'Office',
        master_office: 'Captain / Office',
        authorized: 'Authorised person',
      },
      states: {
        done: 'Done',
        current: 'In progress',
        pending: 'Waiting',
        optional: 'If required',
      },
      stageControl: 'Detailed status',
      readyHint: 'All required work orders are completed. Move the defect to Verification.',
      rejectTitle: 'Reject the repair',
      rejectBody:
        'The defect returns to Under Action. Reported work orders do not change: a follow-up work order is opened when you may open work orders.',
      reason: 'Reason',
    },
    workOrders: {
      table: 'Linked work orders',
      empty: 'No work order for this defect yet.',
      columns: { no: 'WO No', title: 'Title', kind: 'Type', status: 'Status' },
      statuses: {
        planned: 'Planned',
        in_progress: 'In Progress',
        reported: 'Completed',
        cancelled: 'Cancelled',
      },
      kinds: {
        planned: 'Planned',
        unplanned: 'Unplanned',
        corrective: 'Corrective',
        breakdown: 'Breakdown',
      },
      open: 'Open {{no}}',
      hint: 'When every work order (except cancelled ones) is completed, the defect moves to Verification.',
    },
    nc: {
      table: 'Linked non-conformities',
      empty:
        'No non-conformity is linked to this defect. A non-conformity follows its own closure.',
      columns: { no: 'Report No', type: 'Type', title: 'Title', status: 'Status' },
      open: 'Open {{no}}',
    },
    material: {
      used: 'Material used',
      table: 'Material used for this defect',
      empty: 'No material used yet.',
      columns: { material: 'Material', quantity: 'Quantity', date: 'Date', via: 'Recorded via' },
      viaDefect: 'Issued from stock',
      viaWorkOrder: 'Used in {{wo}}',
      notFromStock: 'not deducted – stock correction needed',
      requisitions: 'Purchase requisitions',
      reqTable: 'Linked purchase requisitions',
      reqEmpty: 'No purchase requisition yet. Parts not in stock are requested here.',
      reqColumns: { no: 'Requisition No', date: 'Date', status: 'Status' },
      reqStatuses: {
        draft: 'Draft',
        pending_approval: 'Pending approval',
        office_approval: 'Office approval',
        approved: 'Approved',
        returned: 'Returned',
        cancelled: 'Cancelled',
        po_issued: 'PO issued',
        closed: 'Closed',
        in_progress: 'In progress',
      },
      open: 'Open {{no}}',
      issueTitle: 'Use material from stock',
      item: 'Stock item',
      quantity: 'Quantity',
      note: 'Note',
      onHand: '{{name}} · on hand {{n}} {{unit}}',
      noStock: 'Nothing is in stock. Create a purchase requisition for the missing parts.',
      confirm: 'Deduct from stock',
      cancel: 'Cancel',
      hint: 'The stock movement cannot be changed; a correction is made in Stock.',
    },
    documents: {
      title: 'Related documents',
      hse: 'Source report: {{type}} {{no}}',
      workOrder: 'Source maintenance: {{no}}',
      daily: 'Daily observation of {{no}}',
      job: 'PMS task: {{code}} {{title}}',
      open: 'Open',
    },
    newFrom: {
      hse: 'Reported from {{type}} {{no}}. The report is linked to this defect.',
      workOrder: 'Reported during PMS maintenance {{no}}. The work order is linked as the source.',
    },
  },
  stepper: { label: 'Defect workflow' },
  // Akış ve kapanış onay yolu (ADR 0091)
  route: {
    title: 'Approval route',
    flow: 'Defect flow and closure approval',
    closure: 'Closure approval',
    closureChain: 'Closure approval: {{chain}}',
    office: 'office',
    onBoard: 'on board',
    notes: {
      officeAtClosure:
        'Office approval comes at closure: the vessel works the defect through these stages and submits it for closure after verification.',
      closureOnBoard: 'Closure is approved on board: {{chain}}.',
      escalated:
        'Submitted by the {{shipRole}}: under the four-eyes rule the {{shipRole}} cannot approve their own record, so the closure is decided in the office by the {{role}}.',
      ownEscalated:
        'You submitted this defect. As {{shipRole}} you cannot approve it yourself (four-eyes rule); approval goes to the {{role}} in the office.',
      awaitingShip: 'Awaiting {{role}} approval (on board).',
      awaitingOffice: 'Awaiting office approval: {{role}}.',
      decideHere: 'Your decision is needed: use Approve or Return at the top of this report.',
      decideByRole:
        'A user with the {{role}} role decides the closure in the office with Approve or Return on this report.',
      decidedOnBoard:
        'This step is decided on board by the {{role}}; the office sees the decision after the next synchronisation.',
      ownWaiting: 'You submitted this defect; the {{role}} on board approves the closure.',
      officeWaiting:
        'The office decides the closure. The decision reaches the vessel with the next synchronisation.',
    },
  },
  signatures: {
    crew: 'Signature of Crew Member',
    captain: 'Signature of Captain',
    management: 'Signature of Management',
    reported: 'Reported {{at}}',
    submitted: 'Submitted for closure {{at}}',
    approved: 'Approved {{at}}',
    returned: 'Returned {{at}}',
    pending: 'Pending',
    notRequired: 'Not required',
    notSubmitted: 'Not submitted for closure',
    note: 'Signatures are the approval steps of the closure: the crew member submits, the captain and the management approve in their accounts. No handwritten signature is printed.',
  },
  actions: {
    add: 'Add Action',
    table: 'Corrective actions table',
    edit: 'Edit action {{no}}',
    remove: 'Remove action {{no}}',
    empty: 'No corrective actions yet.',
    columns: {
      no: '#',
      description: 'Action Description',
      responsible: 'Responsible',
      targetOn: 'Target Date',
      status: 'Status',
      completedOn: 'Completed Date',
      evidence: 'Evidence',
      edit: 'Edit',
    },
    dialogTitle: 'Corrective action',
    description: 'Action description',
    responsible: 'Responsible',
    targetOn: 'Target date',
    status: 'Status',
    completedOn: 'Completed date',
    evidence: 'Evidence (report, photo, service report no.)',
    evidenceHint: 'An action is not completed without evidence.',
    cancelReason: 'Reason for cancelling',
    save: 'Save action',
    cancel: 'Cancel',
  },
  followups: {
    add: 'Add Follow-up',
    table: 'Follow-up history table',
    empty: 'No follow-up notes yet.',
    columns: { at: 'Date / Time', remarks: 'Action / Remarks', by: 'By' },
    dialogTitle: 'Follow-up note',
    body: 'Action / remarks',
    hint: 'Follow-up notes cannot be changed after saving.',
    save: 'Add note',
    office: 'Office',
  },
  office: {
    noteTitle: 'Management action',
    commentTitle: 'Office comment',
    body: 'Action / remarks',
    actionOn: 'Date of action',
    hint: 'The note goes to the vessel with the next synchronisation and cannot be changed.',
    save: 'Save',
  },
  dialogs: {
    submitTitle: 'Submit for closure',
    submitBody:
      'Your submission is the crew member’s signature (DM-17). The captain and the management then approve the closure.',
    decideApprove: 'Approve closure',
    decideReturn: 'Return closure',
    decideBody: 'Your decision is your signature on the defect report.',
    reason: 'Reason',
    password: 'Password',
    confirm: 'Sign',
    cancel: 'Cancel',
  },
  pdf: {
    reportNo: 'Record No. {{no}}',
    notClosed: 'NOT CLOSED',
    draftCopy: 'Printed {{at}}',
    page: 'Page {{page}} of {{pages}}',
    error: 'The PDF could not be created.',
    none: '—',
  },
  errors: {
    GENERIC: 'Something went wrong ({{code}}).',
    NETWORK: 'No connection. Try again.',
    UNAUTHENTICATED: 'Your session has ended. Sign in again.',
    FORBIDDEN: 'You are not allowed to do this.',
    NOT_FOUND: 'The defect was not found.',
    VALIDATION_FAILED: 'Check the highlighted fields.',
    ROW_VERSION_CONFLICT: 'Someone else changed this defect. Reload and try again.',
    REAUTHENTICATION_REQUIRED: 'Enter your password to sign.',
    INVALID_CREDENTIALS: 'The password is not correct.',
    INVALID_DEFECT: 'Some values are not valid.',
    DESCRIPTION_REQUIRED: 'Describe the defect (Part A).',
    SYSTEM_REQUIRED: 'Enter the system when no PMS equipment is selected.',
    EQUIPMENT_REQUIRED: 'Select the equipment: breakdowns and repair work orders need equipment.',
    EQUIPMENT_DELETED: 'The equipment has been removed from the PMS.',
    TARGET_BEFORE_REPORTED: 'The target date cannot be before the report date.',
    DATE_BEFORE_REPORTED: 'The date cannot be before the report date.',
    FUTURE_DATE: 'The date cannot be in the future.',
    PROCEDURE_REVIEW_INCOMPLETE: 'Name the procedure and how it can be improved.',
    ACTION_DESCRIPTION_REQUIRED: 'Describe the action.',
    EVIDENCE_REQUIRED: 'An action is not completed without evidence.',
    COMPLETED_DATE_REQUIRED: 'Enter the completed date.',
    CANCEL_REASON_REQUIRED: 'Give a reason for cancelling the action.',
    JOB_EQUIPMENT_MISMATCH: 'The PMS task belongs to other equipment.',
    JOB_DELETED: 'The PMS task has been removed.',
    INVALID_SOURCE: 'Only a daily Defect / Observation report can be converted.',
    ALREADY_CONVERTED: 'This observation has already been converted to a defect.',
    DEFECT_FIXED: 'This field cannot be changed after reporting.',
    INVALID_TRANSITION: 'The defect cannot move back to this stage.',
    DEFECT_CLOSED: 'The defect is closed and locked.',
    DEFECT_PENDING_CLOSURE: 'The closure is waiting for approval; the defect is locked.',
    ALREADY_SUBMITTED: 'The closure has already been submitted.',
    NOT_IN_VERIFICATION: 'Move the defect to “Verification” before submitting.',
    ROOT_CAUSE_REQUIRED: 'Enter the root cause.',
    CORRECTIVE_ACTION_REQUIRED: 'Enter the corrective action.',
    REPAIR_NOT_REPORTED: 'The repair work order has not been reported yet.',
    REPAIR_REQUIRED: 'Report the repair or give the reason why no repair is needed.',
    CONDITION_AFTER_REQUIRED: 'Enter the condition after repair.',
    NOT_OPERATIONAL: 'A defect is not closed while the equipment is not operational.',
    CLASS_NOTIFICATION_REQUIRED: 'Enter the class notification or why it is not required.',
    ACTIONS_OPEN: 'Complete or cancel every corrective action first.',
    CAPTAIN_ACTION_REQUIRED:
      'Part B must be filled by the Master first (action taken by captain); it is required for closure.',
    // Kaptana ait bölüm ya da karar (ADR 0127)
    MASTER_ONLY:
      'Only the Master writes Part B and rejects a repair (ISM Code 5). When no Master is assigned, the deputy set in Settings may do it on behalf of the Master.',
    RECTIFIED_DATE_REQUIRED: 'Enter the date rectified.',
    PROCEDURE_REVIEW_REQUIRED: 'Answer the procedure review question.',
    REPAIR_WORK_ORDER_EXISTS: 'A repair work order already exists.',
    STALE_DECISION: 'The defect changed. Reload before deciding.',
    SELF_APPROVAL: 'You cannot approve your own report.',
    NOT_PENDING: 'Nothing is waiting for a decision.',
    BODY_REQUIRED: 'Write the note.',
    FILE_NOT_UPLOADED: 'Some files have not arrived from the vessel yet.',
    REASON_REQUIRED: 'Give a reason.',
    NEGATIVE_STOCK:
      'Not enough in stock. Request the missing quantity with a purchase requisition.',
    STOCK_ITEM_UNKNOWN: 'The stock item was not found.',
    PROVISION_NOT_SPARE_PART:
      'Provisions cannot be used for a defect; they are issued in Provision.',
    INVALID_STOCK_MOVEMENT: 'Enter a quantity greater than zero.',
    WORK_ORDERS_OPEN: 'Complete or cancel every work order of the defect first.',
  },
};

export const defectsTr: typeof defectsEn = {
  title: 'Arızalar',
  description: 'Ekipman arızasının bildirimi ve giderilmesi (DM-17): bildir, gider, onayla.',
  kinds: { defect: 'Arıza', breakdown: 'Breakdown' },
  stages: {
    reported: 'Bildirildi',
    under_action: 'İşlemde',
    verification: 'Doğrulama',
    closed: 'Kapandı',
  },
  awaitingParts: 'İşlemde – Parça Bekleniyor',
  statuses: {
    open: 'Açık',
    investigating: 'İnceleniyor',
    repair: 'Onarımda',
    testing: 'Giderildi – test',
    verification: 'Doğrulama',
  },
  priorities: { low: 'Düşük', medium: 'Orta', high: 'Yüksek' },
  severities: { minor: 'Küçük', major: 'Büyük', critical: 'Kritik' },
  departments: {
    deck: 'Güverte',
    engine: 'Makine',
    electrical: 'Elektrik',
    navigation: 'Seyir',
    safety: 'Emniyet',
    accommodation: 'Yaşam mahalli',
    galley: 'Kuzine',
    other: 'Diğer',
  },
  conditions: {
    operational: 'Çalışır',
    operational_with_restrictions: 'Kısıtlı çalışır',
    not_operational: 'Çalışmaz',
  },
  actionStatuses: {
    open: 'Açık',
    in_progress: 'Sürüyor',
    completed: 'Tamamlandı',
    cancelled: 'İptal',
  },
  pendingApproval: 'Onay bekliyor',
  pendingApprovalRole: '{{role}} onayı bekleniyor ({{where}})',
  overdue: 'Gecikti',
  list: {
    newDefect: 'Yeni Arıza',
    filters: 'Arıza süzgeçleri',
    search: 'Ara',
    searchPlaceholder: 'No, ekipman, sistem ya da konuma göre ara…',
    status: 'Aşama',
    allStatus: 'Bütün aşamalar',
    priority: 'Öncelik',
    allPriority: 'Bütün öncelikler',
    kind: 'Tür',
    allKinds: 'Bütün türler',
    vessel: 'Gemi',
    allVessels: 'Bütün gemiler',
    dateRange: 'Bildirim aralığı',
    dateFrom: 'Başlangıç',
    dateTo: 'Bitiş',
    table: 'Arıza bildirimleri',
    open: '{{no}} aç',
    emptyTitle: 'Bildirilmiş arıza yok',
    emptyShip: '“Yeni Arıza” ile bildirin; ekipmana şimdi ya da sonra bağlanabilir.',
    emptyWeb: 'Gemide bildirilen arızalar bir sonraki senkronda burada görünür.',
    noMatch: 'Süzgeçlere uyan arıza yok.',
    showing: '{{total}} kayıttan {{from}}–{{to}}',
    pagination: 'Arıza sayfaları',
    error: 'Arızalar yüklenemedi.',
    retry: 'Yeniden dene',
    kpi: {
      total: 'Toplam',
      open: 'Açık',
      overdue: 'Geciken',
      pending: 'Onay Bekleyen',
      closed: 'Kapanan',
    },
    columns: {
      defectNo: 'Arıza No',
      reported: 'Bildirim',
      vessel: 'Gemi',
      equipment: 'Ekipman / Sistem',
      description: 'Açıklama',
      department: 'Departman',
      priority: 'Öncelik',
      stage: 'Aşama',
      target: 'Hedef',
    },
  },
  record: {
    title: 'Arıza Bildirimi',
    subtitle: 'Ekipman Arıza Bildirimi ve Giderilmesi (DM-17)',
    newTitle: 'Yeni Arıza Bildirimi',
    back: 'Arızalara dön',
    loading: 'Arıza bildirimi yükleniyor…',
    print: 'Formu Yazdır (DM-17)',
    save: 'Kaydet',
    create: 'Arızayı Bildir',
    saved: 'Kaydedildi.',
    unsaved: 'Kaydedilmemiş değişiklikler var.',
    readOnly: 'Salt okunur: arıza gemide yazılır.',
    locked: 'Kilitli: kapanış onay bekliyor ya da onaylandı.',
    stageMenu: 'Aşamayı değiştir',
    moveTo: 'Geçiş: {{status}}',
    sections: 'Bildirim bölümleri',
    tabs: {
      general: '1. Genel',
      crew: '2. Arıza Ayrıntısı (Mürettebat)',
      captain: '3. Giderme (Kaptan)',
      management: '4. Yönetim',
      documentation: '5. Sistem Belgeleri',
      closure: '6. Takip ve Kapanış',
    },
    cards: {
      general: 'Genel Bilgiler',
      partA: 'Bölüm A – Arızanın Tanımı',
      partAHint: '(Mürettebat doldurur)',
      partB: 'Bölüm B – Kaptanın Aldığı Önlem',
      partC: 'Bölüm C – Yönetimin Aldığı Önlem',
      review: 'IMS / SMS – Prosedür Gözden Geçirme (gerekirse)',
      status: 'Durum ve Önemli Tarihler',
      related: 'İlgili Bilgiler',
      signatures: 'İmzalar',
      actions: 'Düzeltici Faaliyetler',
      followups: 'Takip Geçmişi',
      closure: 'İnceleme ve Kapanış',
    },
    fields: {
      recordNo: 'Kayıt No',
      kind: 'Tür',
      dateReported: 'Bildirim Tarihi',
      reportedBy: 'Bildiren',
      position: 'Görevi',
      vessel: 'Gemi',
      imo: 'IMO No',
      department: 'Departman',
      location: 'Gemideki Konum',
      category: 'Makine / Ekipman Kategorisi',
      equipment: 'Ekipman / Sistem',
      equipmentNone: 'PMS’te yok (sistemi yazın)',
      systemName: 'Sistem (PMS’te yok)',
      tag: 'Ekipman Etiket No',
      job: 'Bağlı PMS İşi',
      jobNone: 'Bağlı iş yok',
      severity: 'Ciddiyet',
      operationalImpact: 'Operasyonel etki',
      description: 'Arızanın tanımı',
      captainAction: 'Kaptanın aldığı önlem',
      captainActionOn: 'Önlem Tarihi',
      rectifiedOn: 'Giderilme Tarihi',
      targetOn: 'Hedef Tamamlanma Tarihi',
      closedOn: 'Kapanış Tarihi',
      priority: 'Öncelik',
      reviewQuestion: 'Bu arıza SMS belgelerinde ya da prosedürlerde güncelleme gerektiriyor mu?',
      yes: 'Evet',
      no: 'Hayır',
      procedure: 'Prosedürlerin Adı',
      improvement: 'Prosedür nasıl iyileştirilebilir',
      investigation: 'İnceleme',
      rootCause: 'Kök neden',
      correctiveAction: 'Düzeltici faaliyet (özet)',
      repairWorkOrder: 'Onarım iş emri',
      noRepairReason: 'Onarım gerekmiyor – gerekçe',
      testResult: 'Test sonucu',
      conditionAfter: 'Onarım sonrası durum',
      classNotificationRef: 'Klas bildirim referansı',
      classNotificationWaiver: 'Klas bildirimi gerekmiyor – gerekçe',
      source: 'Kaynak',
    },
    sources: {
      manual: 'Gemide bildirildi',
      measurement: 'Limit dışı ölçüm',
      work_record: 'Bakım kaydı',
      cm_import: 'Durum izleme içe aktarımı',
      daily_report: 'Günlük gözlem',
      hse_report: 'HSEQ raporu',
      drydock: 'Pre-Docking bulgusu',
    },
    observation: '{{day}} günlük gözleminden dönüştürüldü: “{{summary}}”',
    observationLink: 'Günlük raporu aç',
    alreadyConverted: 'Bu gözlem zaten {{no}} arızasına dönüştürüldü.',
    hseTitle: 'Eksik ekipman arıza değildir',
    hseBody:
      'Gemide bulunması gereken ama olmayan, eksik ya da geçerli sertifikası olmayan donanım ISM eksikliğidir. HSE Deficiency olarak bildirin; arıza (DM-17) var olan ekipmanın bozulmasıdır.',
    hseOpen: 'HSE Deficiency aç',
    managementEmpty: 'Yönetimin kaydettiği önlem henüz yok.',
    managementOffice: 'Yönetimin önlemlerini ofis yazar; bir sonraki senkronda gemiye gelir.',
    addManagementAction: 'Yönetim Önlemi Ekle',
    addComment: 'Yorum Ekle',
    comments: 'Ofis yorumları',
    documentsEmpty: 'Henüz fotoğraf ya da belge yok.',
    addPhoto: 'Fotoğraf / Belge Ekle',
    uploading: 'Yükleniyor…',
    repairOpen: 'İş Emri Aç',
    repairNeedsEquipment: 'Onarım iş emri için arızayı bir ekipmana bağlayın.',
    submit: 'Kapanışa Gönder',
    approve: 'Onayla',
    return: 'İade Et',
  },
  // LGS-17 sadeleştirmesi (ADR 0096)
  lgs: {
    eyebrow: 'Arıza Bildirim Formu (LGS-17)',
    created: 'Oluşturulma: {{at}}',
    source: 'Kaynak: {{source}}',
    createdBy: 'Oluşturan: {{name}}',
    print: 'Yazdır',
    pdf: 'PDF',
    sourceDirect: 'Doğrudan arıza bildirimi',
    sourceHse: '{{type}} {{no}}',
    sourceWorkOrder: 'PMS bakımı {{no}}',
    sourceDaily: 'Günlük gözlem {{no}}',
    tabsLabel: 'Arıza raporu sekmeleri',
    tabs: {
      form: 'Form (LGS-17)',
      workflow: 'Workflow / Geçmiş',
      workOrders: 'İş Emirleri ({{n}})',
      nonConformities: 'Uygunsuzluklar ({{n}})',
      material: 'Malzeme ({{n}})',
      photos: 'Fotoğraflar ({{n}})',
      documents: 'İlgili Dokümanlar',
    },
    parts: {
      a: 'BÖLÜM A – Mürettebat',
      aHint: 'Mürettebat tarafından doldurulacaktır',
      b: 'BÖLÜM B – Kaptan',
      bHint: 'Kaptan tarafından doldurulacaktır',
      c: 'BÖLÜM C – İşletme',
      cHint: 'İşletme tarafından doldurulacaktır',
      sms: 'Emniyet Yönetim Sistemi Takibi',
      smsHint: 'SMS prosedür takibi',
      completed: 'Tamamlandı',
      waiting: 'Bekliyor',
    },
    fields: {
      crewName: 'Mürettebat ismi',
      position: 'Pozisyon / Görevi',
      equipment: 'Ekipman',
      location: 'Lokasyon',
      description: 'Arızanın tanımı',
      actionRequired: 'Arızanın onarılması için gereken işlemler',
      actionTaken: 'Arızanın onarılması için yapılan işlemler',
      dateRectified: 'Arızanın onarılma tarihi',
      procedures: 'Prosedür isimleri',
      improvement: 'Prosedürler nasıl geliştirilebilir',
      noUpdate: 'SMS prosedür güncellemesi gerekmiyor',
      crewSignature: 'Mürettebatın imzası',
      captainSignature: 'Kaptanın imzası',
      managementApproval: 'İşletme onayı',
      date: 'Tarih',
      you: 'Siz (giriş yapan kullanıcı)',
    },
    managementShip: 'Bölüm C işletme tarafından yazılır ve sonraki senkronla gelir.',
    photos: 'Fotoğraflar ve dosyalar',
    more: 'Diğer ayrıntılar',
    moreHint:
      'Tür, ciddiyet, öncelik, departman, hedef tarih, PMS işi, inceleme, kök neden, klas bildirimi ve kapanış alanları.',
    side: {
      related: 'İlgili Kayıtlar',
      status: 'Durum',
      steps: 'Basamaklar',
      quick: 'Hızlı İşlemler',
      relatedEmpty: 'Henüz ilgili kayıt yok.',
      current: 'Güncel durum',
      priority: 'Öncelik',
      category: 'Kategori',
      createdBy: 'Oluşturan',
      createdAt: 'Oluşturulma tarihi',
      updatedAt: 'Son güncelleme',
      kinds: {
        workOrder: 'İş Emri',
        nonConformity: 'Uygunsuzluk Raporu',
        requisition: 'Satın Alma Talebi',
        equipment: 'Ekipman',
        source: 'Kaynak',
      },
      quickNone: 'Bu aşamada rolünüz için işlem yok.',
    },
    quick: {
      newWorkOrder: 'Yeni İş Emri Oluştur',
      openNc: 'Uygunsuzluk Raporu Aç',
      newRequisition: 'Satın Alma Talebi Oluştur',
      useStock: 'Stoktan Kullan',
      moveToVerification: 'Doğrulamaya Geçir',
      rejectRepair: 'Onarımı Reddet',
      needsEquipment: 'İş emri açmak için arızayı ekipmana bağlayın.',
    },
    workflow: {
      title: 'Arıza basamakları',
      table: 'Arıza basamakları tablosu',
      columns: {
        no: '#',
        stage: 'Aşama',
        step: 'Basamak',
        owner: 'Sorumlu',
        state: 'Durum',
        when: 'Tarih / Kişi',
      },
      steps: {
        create: 'Arıza oluşturma (Bölüm A)',
        captainReview: 'Kaptan değerlendirmesi (Bölüm B başlangıcı)',
        workOrder: 'İş emri oluşturma',
        nonConformity: 'Uygunsuzluk oluşturma (gerekirse)',
        parts: 'Yedek parça / satın alma',
        execution: 'İş emri yürütme',
        completion: 'İş emri tamamlama',
        verification: 'Kaptan onarım doğrulaması (Bölüm B tamamlanması)',
        management: 'İşletme incelemesi (Bölüm C)',
        sms: 'SMS prosedür takibi',
        closure: 'Arıza kapanışı',
      },
      owners: {
        crew: 'Mürettebat',
        master: 'Kaptan',
        technical: 'Teknik personel',
        technical_office: 'Teknik personel / İşletme',
        office: 'İşletme',
        master_office: 'Kaptan / İşletme',
        authorized: 'Yetkili kişi',
      },
      states: {
        done: 'Tamamlandı',
        current: 'Sürüyor',
        pending: 'Bekliyor',
        optional: 'Gerekirse',
      },
      stageControl: 'Ayrıntılı durum',
      readyHint: 'Gerekli iş emirlerinin hepsi tamamlandı. Arızayı doğrulamaya geçirin.',
      rejectTitle: 'Onarımı reddet',
      rejectBody:
        'Arıza İşlemde aşamasına döner. Raporlanmış iş emri değişmez: iş emri açma yetkiniz varsa takip iş emri açılır.',
      reason: 'Gerekçe',
    },
    workOrders: {
      table: 'Bağlı iş emirleri',
      empty: 'Bu arıza için henüz iş emri yok.',
      columns: { no: 'İş Emri No', title: 'Başlık', kind: 'Tür', status: 'Durum' },
      statuses: {
        planned: 'Planlandı',
        in_progress: 'Sürüyor',
        reported: 'Tamamlandı',
        cancelled: 'İptal',
      },
      kinds: {
        planned: 'Planlı',
        unplanned: 'Plansız',
        corrective: 'Düzeltici',
        breakdown: 'Breakdown',
      },
      open: '{{no}} aç',
      hint: 'İptal edilenler dışındaki bütün iş emirleri tamamlanınca arıza doğrulamaya geçer.',
    },
    nc: {
      table: 'Bağlı uygunsuzluklar',
      empty: 'Bu arızaya bağlı uygunsuzluk yok. Uygunsuzluk kendi kapanış sürecini izler.',
      columns: { no: 'Rapor No', type: 'Tür', title: 'Başlık', status: 'Durum' },
      open: '{{no}} aç',
    },
    material: {
      used: 'Kullanılan malzeme',
      table: 'Arızada kullanılan malzeme',
      empty: 'Henüz malzeme kullanılmadı.',
      columns: { material: 'Malzeme', quantity: 'Miktar', date: 'Tarih', via: 'Kayıt' },
      viaDefect: 'Stoktan düşüldü',
      viaWorkOrder: '{{wo}} içinde kullanıldı',
      notFromStock: 'stoktan düşmedi – stok düzeltmesi gerekiyor',
      requisitions: 'Satın alma talepleri',
      reqTable: 'Bağlı satın alma talepleri',
      reqEmpty: 'Henüz satın alma talebi yok. Stokta olmayan parça buradan talep edilir.',
      reqColumns: { no: 'Talep No', date: 'Tarih', status: 'Durum' },
      reqStatuses: {
        draft: 'Taslak',
        pending_approval: 'Onay bekliyor',
        office_approval: 'Ofis onayında',
        approved: 'Onaylandı',
        returned: 'İade edildi',
        cancelled: 'İptal',
        po_issued: 'Sipariş verildi',
        closed: 'Kapandı',
        in_progress: 'Sürüyor',
      },
      open: '{{no}} aç',
      issueTitle: 'Stoktan malzeme kullan',
      item: 'Stok kalemi',
      quantity: 'Miktar',
      note: 'Not',
      onHand: '{{name}} · eldeki {{n}} {{unit}}',
      noStock: 'Stokta malzeme yok. Eksik parçalar için satın alma talebi açın.',
      confirm: 'Stoktan düş',
      cancel: 'Vazgeç',
      hint: 'Stok hareketi değiştirilemez; düzeltme Stok ekranında yapılır.',
    },
    documents: {
      title: 'İlgili dokümanlar',
      hse: 'Kaynak rapor: {{type}} {{no}}',
      workOrder: 'Kaynak bakım: {{no}}',
      daily: '{{no}} günlük gözlemi',
      job: 'PMS işi: {{code}} {{title}}',
      open: 'Aç',
    },
    newFrom: {
      hse: '{{type}} {{no}} raporundan bildiriliyor. Rapor bu arızaya bağlanır.',
      workOrder: 'PMS bakımı {{no}} sırasında bildiriliyor. İş emri kaynak olarak bağlanır.',
    },
  },
  stepper: { label: 'Arıza akışı' },
  route: {
    title: 'Onay yolu',
    flow: 'Arıza akışı ve kapanış onayı',
    closure: 'Kapanış onayı',
    closureChain: 'Kapanış onayı: {{chain}}',
    office: 'ofis',
    onBoard: 'gemi',
    notes: {
      officeAtClosure:
        'Ofis onayı kapanışta gelir: gemi arızayı bu aşamalardan geçirir ve doğrulamadan sonra kapanışa gönderir.',
      closureOnBoard: 'Kapanış gemide onaylanır: {{chain}}.',
      escalated:
        'Kaydı {{shipRole}} gönderdi: dört göz kuralı gereği {{shipRole}} kendi kaydını onaylayamaz; kapanış ofiste {{role}} tarafından karara bağlanır.',
      ownEscalated:
        'Bu arızayı siz gönderdiniz. {{shipRole}} olarak kendi kaydınızı onaylayamazsınız (dört göz kuralı); onay ofiste {{role}} rolüne gider.',
      awaitingShip: '{{role}} onayı bekleniyor (gemide).',
      awaitingOffice: 'Ofis onayı bekleniyor: {{role}}.',
      decideHere:
        'Kararınız bekleniyor: raporun üstündeki Onayla ya da İade Et düğmesini kullanın.',
      decideByRole:
        'Kapanışı ofiste {{role}} rolündeki kullanıcı bu rapordaki Onayla ya da İade Et ile karara bağlar.',
      decidedOnBoard:
        'Bu adım gemide {{role}} tarafından karara bağlanır; ofis kararı bir sonraki senkronla görür.',
      ownWaiting: 'Bu arızayı siz gönderdiniz; kapanışı gemide {{role}} onaylar.',
      officeWaiting: 'Kapanışı ofis karara bağlar. Karar bir sonraki senkronla gemiye iner.',
    },
  },
  signatures: {
    crew: 'Mürettebatın İmzası',
    captain: 'Kaptanın İmzası',
    management: 'Yönetimin İmzası',
    reported: 'Bildirdi {{at}}',
    submitted: 'Kapanışa gönderdi {{at}}',
    approved: 'Onayladı {{at}}',
    returned: 'İade etti {{at}}',
    pending: 'Bekliyor',
    notRequired: 'Gerekmiyor',
    notSubmitted: 'Kapanışa gönderilmedi',
    note: 'İmzalar kapanışın onay adımlarıdır: mürettebat gönderir, kaptan ve yönetim kendi hesaplarıyla onaylar. Islak imza basılmaz.',
  },
  actions: {
    add: 'Faaliyet Ekle',
    table: 'Düzeltici faaliyetler tablosu',
    edit: '{{no}}. faaliyeti düzenle',
    remove: '{{no}}. faaliyeti kaldır',
    empty: 'Henüz düzeltici faaliyet yok.',
    columns: {
      no: '#',
      description: 'Faaliyet',
      responsible: 'Sorumlu',
      targetOn: 'Hedef Tarih',
      status: 'Durum',
      completedOn: 'Tamamlanma',
      evidence: 'Kanıt',
      edit: 'Düzenle',
    },
    dialogTitle: 'Düzeltici faaliyet',
    description: 'Faaliyet',
    responsible: 'Sorumlu',
    targetOn: 'Hedef tarih',
    status: 'Durum',
    completedOn: 'Tamamlanma tarihi',
    evidence: 'Kanıt (rapor, fotoğraf, servis raporu no)',
    evidenceHint: 'Faaliyet kanıtsız tamamlanmaz.',
    cancelReason: 'İptal gerekçesi',
    save: 'Faaliyeti kaydet',
    cancel: 'Vazgeç',
  },
  followups: {
    add: 'Takip Notu Ekle',
    table: 'Takip geçmişi tablosu',
    empty: 'Henüz takip notu yok.',
    columns: { at: 'Tarih / Saat', remarks: 'Yapılan / Açıklama', by: 'Yazan' },
    dialogTitle: 'Takip notu',
    body: 'Yapılan / açıklama',
    hint: 'Takip notu kaydedildikten sonra değiştirilemez.',
    save: 'Notu ekle',
    office: 'Ofis',
  },
  office: {
    noteTitle: 'Yönetim önlemi',
    commentTitle: 'Ofis yorumu',
    body: 'Yapılan / açıklama',
    actionOn: 'Önlem tarihi',
    hint: 'Not bir sonraki senkronda gemiye gider ve değiştirilemez.',
    save: 'Kaydet',
  },
  dialogs: {
    submitTitle: 'Kapanışa gönder',
    submitBody:
      'Gönderiminiz mürettebatın imzasıdır (DM-17). Ardından kaptan ve yönetim kapanışı onaylar.',
    decideApprove: 'Kapanışı onayla',
    decideReturn: 'Kapanışı iade et',
    decideBody: 'Kararınız arıza bildirimindeki imzanızdır.',
    reason: 'Gerekçe',
    password: 'Parola',
    confirm: 'İmzala',
    cancel: 'Vazgeç',
  },
  pdf: {
    reportNo: 'Kayıt No {{no}}',
    notClosed: 'KAPANMADI',
    draftCopy: 'Basım {{at}}',
    page: 'Sayfa {{page}} / {{pages}}',
    error: 'PDF oluşturulamadı.',
    none: '—',
  },
  errors: {
    GENERIC: 'Bir sorun oluştu ({{code}}).',
    NETWORK: 'Bağlantı yok. Yeniden deneyin.',
    UNAUTHENTICATED: 'Oturumunuz kapandı. Yeniden giriş yapın.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Arıza bulunamadı.',
    VALIDATION_FAILED: 'İşaretli alanları kontrol edin.',
    ROW_VERSION_CONFLICT: 'Arızayı başka biri değiştirdi. Yenileyip tekrar deneyin.',
    REAUTHENTICATION_REQUIRED: 'İmzalamak için parolanızı girin.',
    INVALID_CREDENTIALS: 'Parola yanlış.',
    INVALID_DEFECT: 'Bazı değerler geçersiz.',
    DESCRIPTION_REQUIRED: 'Arızayı tanımlayın (Bölüm A).',
    SYSTEM_REQUIRED: 'PMS ekipmanı seçilmediyse sistemi yazın.',
    EQUIPMENT_REQUIRED: 'Ekipmanı seçin: breakdown ve onarım iş emri ekipman ister.',
    EQUIPMENT_DELETED: 'Ekipman PMS’ten kaldırılmış.',
    TARGET_BEFORE_REPORTED: 'Hedef tarih bildirim tarihinden önce olamaz.',
    DATE_BEFORE_REPORTED: 'Tarih bildirim tarihinden önce olamaz.',
    FUTURE_DATE: 'Tarih gelecekte olamaz.',
    PROCEDURE_REVIEW_INCOMPLETE: 'Prosedürü ve nasıl iyileştirileceğini yazın.',
    ACTION_DESCRIPTION_REQUIRED: 'Faaliyeti tanımlayın.',
    EVIDENCE_REQUIRED: 'Faaliyet kanıtsız tamamlanmaz.',
    COMPLETED_DATE_REQUIRED: 'Tamamlanma tarihini girin.',
    CANCEL_REASON_REQUIRED: 'Faaliyetin iptal gerekçesini yazın.',
    JOB_EQUIPMENT_MISMATCH: 'PMS işi başka bir ekipmanın.',
    JOB_DELETED: 'PMS işi kaldırılmış.',
    INVALID_SOURCE: 'Yalnız günlük Defect / Observation raporu dönüştürülür.',
    ALREADY_CONVERTED: 'Bu gözlem zaten arızaya dönüştürüldü.',
    DEFECT_FIXED: 'Bu alan bildirimden sonra değiştirilemez.',
    INVALID_TRANSITION: 'Arıza bu aşamaya geri dönemez.',
    DEFECT_CLOSED: 'Arıza kapandı ve kilitli.',
    DEFECT_PENDING_CLOSURE: 'Kapanış onay bekliyor; arıza kilitli.',
    ALREADY_SUBMITTED: 'Kapanış zaten gönderildi.',
    NOT_IN_VERIFICATION: 'Göndermeden önce arızayı “Doğrulama” aşamasına alın.',
    ROOT_CAUSE_REQUIRED: 'Kök nedeni yazın.',
    CORRECTIVE_ACTION_REQUIRED: 'Düzeltici faaliyeti yazın.',
    REPAIR_NOT_REPORTED: 'Onarım iş emri henüz raporlanmadı.',
    REPAIR_REQUIRED: 'Onarımı raporlayın ya da onarım gerekmeme gerekçesini yazın.',
    CONDITION_AFTER_REQUIRED: 'Onarım sonrası durumu girin.',
    NOT_OPERATIONAL: 'Ekipman çalışmazken arıza kapanmaz.',
    CLASS_NOTIFICATION_REQUIRED: 'Klas bildirimini ya da gerekmeme gerekçesini girin.',
    ACTIONS_OPEN: 'Önce her düzeltici faaliyeti tamamlayın ya da iptal edin.',
    CAPTAIN_ACTION_REQUIRED:
      'Önce kaptan Bölüm B’yi (kaptanın önlemi) doldurmalı; kapanış için gerekli.',
    // Kaptana ait bölüm ya da karar (ADR 0127)
    MASTER_ONLY:
      'Bölüm B’yi yalnız kaptan yazar ve onarımı yalnız kaptan reddeder (ISM Kod 5). Kaptan ataması yokken Ayarlar’da seçilen vekil kaptan adına yapabilir.',
    RECTIFIED_DATE_REQUIRED: 'Giderilme tarihini girin.',
    PROCEDURE_REVIEW_REQUIRED: 'Prosedür gözden geçirme sorusunu cevaplayın.',
    REPAIR_WORK_ORDER_EXISTS: 'Onarım iş emri zaten var.',
    STALE_DECISION: 'Arıza değişti. Karar vermeden önce yenileyin.',
    SELF_APPROVAL: 'Kendi bildiriminizi onaylayamazsınız.',
    NOT_PENDING: 'Karar bekleyen bir şey yok.',
    BODY_REQUIRED: 'Notu yazın.',
    FILE_NOT_UPLOADED: 'Bazı dosyalar gemiden henüz gelmedi.',
    REASON_REQUIRED: 'Gerekçe yazın.',
    NEGATIVE_STOCK: 'Stok yetmiyor. Eksik miktarı satın alma talebiyle isteyin.',
    PROVISION_NOT_SPARE_PART: 'Kumanya arızada kullanılamaz; Kumanya ekranından düşülür.',
    STOCK_ITEM_UNKNOWN: 'Stok kalemi bulunamadı.',
    INVALID_STOCK_MOVEMENT: 'Sıfırdan büyük bir miktar girin.',
    WORK_ORDERS_OPEN: 'Önce arızanın bütün iş emirlerini tamamlayın ya da iptal edin.',
  },
};
